// src/components/ContactFaxes.jsx
// 3.71.0 (owner, Oct 30): Contacts → click a Sent, Received or Failed count
// to see those faxes (GET /portal/contacts/faxes), newest first; a row
// opens the usual fax panel. The counts and this list follow the same rule
// (Managers their teams', Users their own sent faxes; received by the
// Inbox's line rule). Fax files are purged after 90 days and records kept 24
// months, so older rows say "File purged" (owner's word, 3.71.1) and the
// panel shows the details only.
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from '../firebase/config';
import { formatFax, formatDate, STATUS_COLORS, outStatus } from '../utils/format';
import { reasonText, incomingReasonText } from '../utils/faxReasons';
import { PILL, PILL_ON, PILL_ROW } from '../theme/tabs';
import ShowSelect from './ShowSelect';
import Pager, { usePager } from './Pager';
import Loader from './Loader';
import Emo from './Emo';
import FaxDetailPanel from './FaxDetailPanel';
import { usePhone } from '../hooks/useWindowWidth';

const KINDS = [['sent', 'Sent'], ['received', 'Received'], ['failed', 'Failed']];

// contact: a Contacts row; kind: the count clicked
export default function ContactFaxes({ contact, kind: firstKind, multi, onClose }) {
  const { getToken } = useAuth();
  const phone = usePhone();
  const [kind, setKind] = useState(firstKind);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [show, setShow] = useState('50');
  const [open, setOpen] = useState(null);   // { type, row } in the fax panel
  const counts = { sent: contact.sent, received: contact.received + contact.receiveFailed, failed: contact.failed + contact.receiveFailed };

  useEffect(() => {
    let live = true;
    setData(null); setError('');
    (async () => {
      try {
        const q = new URLSearchParams({ account: contact.accountId, number: contact.number, kind });
        const r = await fetch(`${API_BASE}/portal/contacts/faxes?${q}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Couldn't load these faxes.");
        if (live) setData(j);
      } catch (e) { if (live) setError(e.message); }
    })();
    return () => { live = false; };
  }, [contact.accountId, contact.number, kind, getToken]);

  // Escape closes the list (the fax panel handles its own)
  useEffect(() => {
    if (open) return undefined;
    const k = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);

  const rows = data?.data || [];
  const { pageRows, pager } = usePager(rows, show);
  const name = contact.name && contact.name !== contact.number ? contact.name : '';

  // The fax panel opens in place of the list; closing it comes back here
  if (open) {
    return <FaxDetailPanel type={open.type} row={open.row} onClose={() => setOpen(null)} />;
  }

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-label={`Faxes with ${formatFax(contact.number)}`}>
        <div style={S.head} className="dlg-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={S.title}><Emo e="📇" /> {formatFax(contact.number)}</strong>
            <div style={S.sub}>{[name, multi && contact.accountName].filter(Boolean).join(' · ')}</div>
          </div>
          <button onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>
        <div style={S.bar}>
          <div style={PILL_ROW} role="tablist" aria-label="Which faxes">
            {KINDS.map(([k, label]) => (
              <button key={k} role="tab" aria-selected={kind === k} onClick={() => setKind(k)} style={kind === k ? PILL_ON : PILL}>{label} {counts[k] || 0}</button>
            ))}
          </div>
          <ShowSelect value={show} onChange={setShow} options={['50', '100']} />
        </div>
        <div style={S.note}>Click a fax to open it. Fax files are purged after {data?.fileDays || 90} days. Older faxes still show their details, but the file can't be opened.</div>
        <div style={S.body}>
          {error ? <div role="alert" style={S.error}>{error}</div>
            : !data ? <Loader label="Loading faxes…" />
            : !rows.length ? <div style={S.empty}>No {kind} faxes with this number{kind === 'sent' ? ' that you can see' : ''}.</div>
            : phone ? (
              <div style={S.cards}>
                {pageRows.map(x => <Card key={x.type + x.row.faxId} x={x} onOpen={() => setOpen({ type: x.type, row: x.row })} />)}
              </div>
            ) : (
              <table style={S.table}>
                <thead><tr style={S.thead}>{['Date', '', 'Status', 'Document', 'Pages', kind === 'sent' ? 'Sent by' : 'Sent by / to line', 'File'].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {pageRows.map((x, i) => {
                    const f = x.row, out = x.type === 'outgoing';
                    const st = out ? outStatus(f) : f.status || 'received';
                    return (
                      <tr key={x.type + f.faxId} onClick={() => setOpen({ type: x.type, row: f })} style={{ ...S.tr, background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }} className="ov-row"
                        tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') setOpen({ type: x.type, row: f }); }} aria-label={`Open the ${out ? 'sent' : 'received'} fax of ${formatDate(x.at)}`}>
                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatDate(x.at)}</td>
                        <td style={S.td} title={out ? 'Sent' : 'Received'}><Emo e={out ? '📤' : '📥'} /></td>
                        <td style={S.td}>
                          <span style={{ ...S.badge, ...(STATUS_COLORS[st] || STATUS_COLORS.queued) }}>{st}</span>
                          {st === 'failed' && <div style={S.reason}>{out ? reasonText(f.failureReason) : incomingReasonText(f.failureReason)}</div>}
                        </td>
                        <td style={{ ...S.td, maxWidth: 260, overflowWrap: 'anywhere' }}>{f.failedIncoming ? '—' : f.fileName || '—'}</td>
                        <td style={S.td}>{f.failedIncoming ? '—' : f.pages ?? '—'}</td>
                        <td style={{ ...S.td, fontSize: 12 }}>{out ? (f.fromName || f.fromEmail || '—') : formatFax(f.toNumber)}</td>
                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fileTag(x)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
        {rows.length > 0 && <div style={{ padding: '0 18px 14px' }}><Pager {...pager} label="faxes" /></div>}
      </div>
    </div>
  );
}

const fileTag = x => (x.row.failedIncoming ? <span style={S.muted}>No document</span>
  : x.fileKept ? <span style={S.kept}>Available</span> : <span style={S.gone} title="Fax files are purged after 90 days">File purged</span>);

function Card({ x, onOpen }) {
  const f = x.row, out = x.type === 'outgoing';
  const st = out ? outStatus(f) : f.status || 'received';
  return (
    <button onClick={onOpen} style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}><Emo e={out ? '📤' : '📥'} /> {formatDate(x.at)}</span>
        <span style={{ ...S.badge, ...(STATUS_COLORS[st] || STATUS_COLORS.queued) }}>{st}</span>
      </div>
      {st === 'failed' && <div style={S.reason}>{out ? reasonText(f.failureReason) : incomingReasonText(f.failureReason)}</div>}
      <div style={S.cardMeta}>{[!f.failedIncoming && f.fileName, !f.failedIncoming && f.pages && `${f.pages} pages`, out ? (f.fromName || f.fromEmail) : `To ${formatFax(f.toNumber)}`].filter(Boolean).join(' · ')}</div>
      <div style={{ marginTop: 4 }}>{fileTag(x)}</div>
    </button>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px 16px' },
  modal: { background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(1000px, 100%)', maxHeight: '84vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)' },
  head: { display: 'flex', gap: 10, alignItems: 'center', padding: '14px 18px', borderBottom: '2px solid var(--c-e0f2f1)' },
  title: { color: 'var(--t-00695c)', fontSize: 16, fontFamily: 'monospace' },
  sub: { fontSize: 13, color: 'var(--t-546e7a)', marginTop: 2 },
  closeBtn: { width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)', cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit', flexShrink: 0 },
  bar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 18px 4px' },
  note: { padding: '4px 18px 8px', fontSize: 12.5, color: 'var(--t-607d8b)' },
  body: { padding: '0 18px 12px', overflow: 'auto', flex: 1, minHeight: 120 },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 760 },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '11px 12px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid var(--c-f0f0f0)', cursor: 'pointer' },
  td: { padding: '10px 12px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '3px 9px', borderRadius: 10, fontSize: 11.5, fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap' },
  reason: { fontSize: 11.5, color: 'var(--t-b71c1c)', marginTop: 3, maxWidth: 240 },
  kept: { fontSize: 12, color: 'var(--t-2e7d32)', fontWeight: 600 },
  gone: { fontSize: 11.5, fontWeight: 700, color: 'var(--t-e65100)', background: 'var(--c-fff3e0)', borderRadius: 8, padding: '2px 8px' },
  muted: { fontSize: 12, color: 'var(--t-90a4ae)' },
  empty: { padding: '26px 4px', textAlign: 'center', color: 'var(--t-78909c)', fontSize: 14 },
  error: { padding: 12, color: 'var(--t-b71c1c)', fontSize: 13 },
  cards: { display: 'flex', flexDirection: 'column', gap: 8 },
  card: { textAlign: 'left', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, padding: '10px 12px', background: 'var(--c-f9fafb)', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--t-263238)', width: '100%' },
  cardMeta: { fontSize: 12, color: 'var(--t-546e7a)', marginTop: 4, overflowWrap: 'anywhere' },
};
